import React, { useState } from 'react';
import BailSummary from './BailSummary';
import AccountingSummary from './AccountingSummary';
import Notes from './Notes';
import TransactionHistory from './TransactionHistory';
import LinkButtons from './LinkButtons';
import useFinancialData from '../hooks/useFinancialData';
import apiService from '../services/api';
import { getAmountClass } from '../utils/formatters';

const FinancialReport = ({ tenantId = 'recIFoFzqbzxfloEE' }) => {
  const { data, loading, error, partialLoading, refetch, partialRefetch } = useFinancialData(tenantId);

  // State for partial loading during closure status change
  const [isPartialLoading, setIsPartialLoading] = useState(false);

  // Handle closure status change
  const handleClotureChange = async (isChecked) => {
    console.log("handleClotureChange appelé avec:", isChecked);
    console.log("Data disponible:", data);
    
    // Activer l'indicateur de chargement partiel
    setIsPartialLoading(true);
    
    if (!data || !data.locataire) {
      console.error("Données manquantes pour la mise à jour du statut de clôture:",
                  { dataExists: !!data, locataireExists: !!(data && data.locataire) });
      setIsPartialLoading(false);
      return;
    }
    
    // Si l'ID du locataire est manquant mais qu'on a les données, on utilise l'ID du tenant
    const locataireId = data.locataire.id || tenantId;
    if (!locataireId) {
      console.error("Impossible de déterminer l'ID du locataire");
      setIsPartialLoading(false);
      return;
    }
    
    try {
      console.log("Mise à jour du statut de clôture pour locataire:", locataireId);
      await apiService.updateClotureStatus(locataireId, isChecked);
      console.log("Appel API réussi, rafraîchissement des données...");
      
      // Utiliser partialRefetch au lieu de refetch
      await partialRefetch();
    } catch (error) {
      console.error('Error updating closure status:', error);
    } finally {
      // Désactiver l'indicateur de chargement avec un léger délai
      setTimeout(() => setIsPartialLoading(false), 500);
    }
  };

  if (loading) {
    return (
      <main className="container">
        <article aria-busy="true" className="text-center">
          <header>
            <h2>Chargement des données financières...</h2>
          </header>
          <p className="secondary">
            Veuillez patienter pendant que nous récupérons les informations du locataire.
          </p>
        </article>
      </main>
    );
  }

  if (error) {
    return (
      <main className="container">
        <article className="text-center">
          <header>
            <h2 className="contrast">Erreur lors du chargement des données</h2>
          </header>
          <p>
            {error.toString()}
          </p>
          <footer>
            <button 
              className="secondary" 
              onClick={refetch}
            >
              Réessayer
            </button>
          </footer>
        </article>
      </main>
    );
  }

  if (!data || !data.finance || !data.locataire || !data.bail) {
    return (
      <main className="container">
        <article className="text-center">
          <header>
            <h2>Données incomplètes</h2>
          </header>
          <p className="secondary">
            Les données requises pour afficher le rapport sont incomplètes ou mal formatées.
          </p>
          <footer>
            <button 
              className="secondary" 
              onClick={refetch}
            >
              Réessayer
            </button>
          </footer>
        </article>
      </main>
    );
  }

  // Initialize finance structure with defaults if any property is missing
  const finance = {
    soldes: {
      courant: 0,
      courantV2: 0,
      final: 0,
      finalV2: 0,
      ...data.finance?.soldes,
    },
    totaux: {
      factures: {
        total: 0,
        regular: 0,
        v2: 0,
        details: { 
          caution: 0, 
          fraisDossier: 0, 
          fraisDossierV2: 0, 
          loyer: 0, 
          loyerV2: 0,
          remise: 0,
          remiseV2: 0,
          reparations: 0,
          reparationsV2: 0,
          // Ensure factureCloture is included in the details
          factureCloture: data.bail?.montants?.factureCloture || 0,
          factureClotureV2: 0
        },
        ...data.finance?.totaux?.factures,
      },
      paiements: {
        total: 0,
        regular: 0,
        v2: 0,
        ...data.finance?.totaux?.paiements,
      },
    },
    regularisation: data.finance?.regularisation || {},
    indicateurs: data.finance?.indicateurs || {},
    ...data.finance,
  };

  // Make sure factureCloture is included in the details if it wasn't in the API response
  if (!finance.totaux.factures.details.factureCloture) {
    finance.totaux.factures.details.factureCloture = data.bail?.montants?.factureCloture || 0;
  }

  // Get classes for different types of balances
  const soldeCourantClass = getAmountClass(finance.soldes.courant);
  const soldeCourantV2Class = getAmountClass(finance.soldes.courantV2);
  const soldeFinalClass = getAmountClass(finance.soldes.final);
  const v2SoldeClass = getAmountClass(finance.soldes.finalV2);

  const locataire = data.locataire;
  const bail = data.bail;
  const soldeCumulatif = data.soldeCumulatif || { total: 0 };
  const analyse = data.analyse || { actionAutomatique: "Aucune action recommandée." };
  
  // No need for a local partialRefetch function as we're using the one from the hook

  return (
    <main className="container">
      <input type="hidden" id="locataireID" value={locataire.id} />
      
      {/* Report title section - Moved to App-level Header */}
      <h2 className="tenant-name">
        <a
          href={`https://airtable.com/app7XWoxwjvdzoSQ1/tblXhIC7mEnGkFfJ3/viwvlMi1lJpSbyYUX/${locataire.id}?blocks=hide`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          Rapport financier: {locataire.nom}
          {locataire.nomCaution && ` (Caution: ${locataire.nomCaution})`}
          {locataire.flags?.version && (
            <span className="version-badge v2-badge">{locataire.flags.version}</span>
          )}
        </a>
      </h2>
      
      <div className="grid">
        {/* Bail Summary section */}
        <BailSummary
          bail={bail}
          locataire={locataire}
          onUpdate={partialRefetch}  // Utiliser partialRefetch au lieu de refetch
          estCloture={locataire.flags?.estCloture}
          onClotureChange={handleClotureChange}
          isLoading={isPartialLoading}
        />
        
        {/* Accounting Summary section */}
        <AccountingSummary
  finance={finance}
  bail={bail}
/>
      </div>
      
      {/* Notes section */}
      <Notes 
        locataireId={locataire.id}
        notes={locataire.notes}
      />
      
      {/* Transaction History section */}
      <TransactionHistory
        data={{
          ...data,
          finance
        }}
        onRefresh={partialRefetch}  // Utilise partialRefetch au lieu de refetch
        isLoading={partialLoading}
      />
      
      {/* Link Buttons section */}
      <LinkButtons
        bailID={bail.id}
        urlFacturesList={locataire.urls?.factures}
        urlPaiement={locataire.urls?.paiements}
        onRefresh={partialRefetch}  // Ajout de cette prop
      />
      {/* API Information Footer */}
      <footer className="api-info-footer" style={{ marginTop: '2rem', fontSize: '0.8rem', color: '#666', textAlign: 'center', padding: '1rem', borderTop: '1px solid #eee' }}>
        <p>
          API VPS: {' '}
          <a
            href={`https://admin.hlwc.fr/public/tenant/${tenantId}/financial-report`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline', color: '#0366d6' }}
          >
            <code>{`https://admin.hlwc.fr/public/tenant/${tenantId}/financial-report`}</code>
          </a>
        </p>
      </footer>
    </main>
  );
};

export default FinancialReport;